import React from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { cn } from '../../lib/utils';
import { Button } from './Button';
import { Modal, ModalHeader, ModalTitle, ModalContent, ModalFooter } from './Modal';

interface ErrorDialogProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  message?: string;
  details?: Record<string, unknown> | string | null;
  /** Label for the single dismiss button */
  closeText?: string;
  /** Use when the dialog must appear above other overlays */
  zIndex?: number;
}

const ErrorDialog: React.FC<ErrorDialogProps> = ({
  isOpen,
  onClose,
  title = 'Error',
  message = 'Something went wrong.',
  details = null,
  closeText = 'OK',
  zIndex,
}) => {
  const { colors } = useTheme();
  if (!isOpen) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} zIndex={zIndex}>
      <ModalHeader>
        <ModalTitle>
          <div className="flex items-center gap-3">
            <span
              className={cn(
                'inline-flex h-8 w-8 shrink-0 items-center justify-center text-rose-700 dark:text-rose-300',
              )}
              aria-hidden="true"
            >
              <svg className="h-4.5 w-4.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </span>
            <span className="block text-left">{title}</span>
          </div>
        </ModalTitle>
      </ModalHeader>
      <ModalContent>
        <div className="space-y-4 text-left">
          <div
            className={cn(
              'rounded-lg border px-3.5 py-3',
              'border-rose-200 bg-rose-50/80 dark:border-rose-900/60 dark:bg-rose-950/30',
            )}
            role="alert"
            aria-live="assertive"
          >
            <div className="flex items-start gap-2.5">
              <span className="mt-0.5 shrink-0 text-rose-600 dark:text-rose-300" aria-hidden="true">
                <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </span>
              <div className="min-w-0">
                <p className="text-xs font-semibold uppercase tracking-wide text-rose-700 dark:text-rose-200">Error</p>
                <p
                  className={cn(
                    'mt-1 min-w-0 max-w-full text-left text-sm leading-relaxed text-rose-800 dark:text-rose-100',
                    'break-words whitespace-pre-wrap [overflow-wrap:anywhere]',
                  )}
                >
                  {message}
                </p>
              </div>
            </div>
          </div>

          {details ? (
            <div className={cn('rounded-lg border p-3.5', colors.surfaceHover, colors.border)}>
              <p className={cn('mb-2 text-[11px] font-semibold uppercase tracking-wide', colors.textSecondary)}>
                Details
              </p>
              <div
                className={cn(
                  'text-left text-xs leading-relaxed',
                  colors.textSecondary,
                  'break-words whitespace-pre-wrap [overflow-wrap:anywhere]',
                )}
              >
                {typeof details === 'object' ? (
                  Object.entries(details).map(([key, value]) => (
                    <div key={key} className="max-w-full">
                      <strong>{key}:</strong> {String(value)}
                    </div>
                  ))
                ) : (
                  <div className="max-w-full">{details}</div>
                )}
              </div>
            </div>
          ) : null}
        </div>
      </ModalContent>
      <ModalFooter>
        <div className="flex w-full justify-end">
          <Button type="button" onClick={onClose} variant="default">
            {closeText}
          </Button>
        </div>
      </ModalFooter>
    </Modal>
  );
};

export default ErrorDialog;
